"use client";
import { useEffect } from "react";
import { useAuth } from "../../util/auth";
import { redeemAdminHandoff } from "../../api/adminSession";
import { isAdminHost } from "../../lib/adminHosts";
import { logger } from "../../util/logger";

/**
 * Seamless SSO bootstrap for the IAP-protected admin origin.
 *
 * When the app loads on an admin host and no Firebase session exists, it redeems a
 * single-use hand-off code (delivered by the public origin via a parent-domain
 * HttpOnly cookie) and signs in with the resulting custom token, removing the second
 * manual login after IAP authentication. On any failure (no/expired hand-off) it
 * silently no-ops so the normal login UI still works.
 *
 * The hand-off code is single-use: redeeming it consumes the code and clears the
 * cookie server-side. Redemption must therefore fire at most once per tab, or a
 * second attempt (from a remount or a full page reload during the admin landing)
 * would hit a guaranteed 401 and, if it races a slow reload, drop the fresh session.
 *
 * Renders nothing.
 */

// Tab-scoped marker so redemption survives full page reloads within the same tab.
const HANDOFF_ATTEMPT_KEY = "nrs_admin_handoff_attempted";

// Module-scoped flag: guards synchronous double-invocation within a single page
// load (e.g., a remount in the same tick, before a sessionStorage write is
// observable). sessionStorage extends the guard across remounts and reloads so the
// single-use hand-off code is redeemed at most once per tab.
let handoffAttemptedThisLoad = false;

function handoffAlreadyAttempted() {
  if (handoffAttemptedThisLoad) {
    return true;
  }
  return window.sessionStorage.getItem(HANDOFF_ATTEMPT_KEY) === "1";
}

function markHandoffAttempted() {
  handoffAttemptedThisLoad = true;
  window.sessionStorage.setItem(HANDOFF_ATTEMPT_KEY, "1");
}

export default function AdminSessionBootstrap() {
  const { user, callSignInWithCustomToken } = useAuth();

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }
    if (!isAdminHost(window.location.hostname)) {
      return;
    }
    // `false` means auth has resolved to "signed out"; `null`/`undefined` means the
    // auth listener has not settled yet. Only attempt redemption once definitively
    // signed out so we neither race the listener nor clobber an existing session.
    if (user !== false) {
      return;
    }
    if (handoffAlreadyAttempted()) {
      return;
    }
    // Mark before the await so a remount in the same tick cannot double-redeem the
    // single-use code (which would 401 and, on a slow reload, drop the session).
    markHandoffAttempted();

    (async () => {
      try {
        const response = await redeemAdminHandoff();
        const token = response?.data?.access_token;
        if (typeof token === "string" && token.length > 0) {
          // Consent was already enforced on the public origin during login; the
          // admin origin does not re-gate consent, so pass false here.
          await callSignInWithCustomToken(token, false);
          logger("Admin session established via hand-off.");
        }
      } catch (error) {
        // No/expired hand-off: fall back to manual login. Do not disrupt the UI.
        logger("No admin hand-off to redeem; manual login required.", error);
      }
    })();
  }, [user, callSignInWithCustomToken]);

  return null;
}
